import { Component, forwardRef, Input, OnInit, ViewChild } from '@angular/core';
import { NgbDate, NgbDateStruct, NgbPopover, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { NgbDatepickerNavigateEvent } from '@ng-bootstrap/ng-bootstrap/datepicker/datepicker';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { noop } from 'rxjs';

export type TDateTimePicker = 'date' | 'time' | 'date-time';
export type TDateTimeFormat = 'medium' | 'mediumDate' | 'mediumTime' | 'shortTime' | 'HH:mm'; // see Angular's DatePipe

@Component({
    selector: 'app-datetime-picker',
    templateUrl: './datetime-picker.component.html',
    styleUrls: ['./datetime-picker.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatetimePickerComponent),
            multi: true
        }
    ]
})
export class DatetimePickerComponent implements ControlValueAccessor, OnInit {
    dateTime: Date;
    private _dateStruct: NgbDateStruct;
    private _timeStruct: NgbTimeStruct;
    public inputIcon: 'fa fa-calendar' | 'fa fa-clock' = null;
    onTouched: () => void = noop;
    onChange: (_: any) => void = noop;
    @Input() disabled: boolean = false;
    @Input() pick: TDateTimePicker = 'date';
    @Input() format: TDateTimeFormat = 'mediumDate';
    @Input() placeholder: string = '';
    @Input() minDate: NgbDateStruct;
    @Input() pickSeconds: boolean = true;
    @ViewChild('popInput') popInput: NgbPopover;

    private static getNgbTimeStructFromDate(value: Date): NgbTimeStruct {
        if (value == null) {
            return null;
        }
        if (!(value instanceof Date)) {
            console.error(`Expected Date instance: current date will be used instead`);
            value = new Date();
        }
        return {
            hour: value.getHours(),
            minute: value.getMinutes(),
            second: value.getSeconds()
        };
    }

    private static getNgbDateStructFromDate(value: Date): NgbDateStruct {
        if (value == null) {
            return null;
        }
        if (!(value instanceof Date)) {
            console.error(`Expected Date instance: current date wil be used instead`);
            value = new Date();
        }
        return {
            year: value.getFullYear(),
            month: value.getMonth() + 1,
            day: value.getDate()
        };
    }

    private static isValid(d: NgbDateStruct, t: NgbTimeStruct): boolean {
        const date = DatetimePickerComponent.getDateFromDateStructAndTimeStruct(
            d,
            t
        );
        return (
            date.getFullYear() === d.year &&
            date.getMonth() + 1 === d.month &&
            date.getDate() === d.day
        );
    }

    static getDateFromDateStructAndTimeStruct(date: NgbDateStruct, time: NgbTimeStruct) {
        if (date == null) {
            date = DatetimePickerComponent.getNgbDateStructFromDate(new Date());
        }
        if (time == null) {
            time = DatetimePickerComponent.getNgbTimeStructFromDate(new Date());
        }
        return new Date(date.year, date.month - 1, date.day, time.hour, time.minute, time.second);
    }

    constructor() {
    }

    ngOnInit(): void {
        if (this.pick === 'time') {
            this.inputIcon = 'fa fa-clock';
        } else {
            this.inputIcon = 'fa fa-calendar';
        }
    }

    @Input()
    markDisabled: (date: NgbDate, current: { year: number; month: number }) => boolean = (
        date,
        current
    ) => false;

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    writeValue(value: Date) {
        console.debug(`writeValue`, value);
        if (value == null) {
            this._setDateTimeToNull();
        } else {
            if (!(value instanceof Date)) {
                console.error(`Expected Date instance: current date wil be used instead`);
                value = new Date();
            }
            this.dateStruct = DatetimePickerComponent.getNgbDateStructFromDate(value);
            this.timeStruct = DatetimePickerComponent.getNgbTimeStructFromDate(value);
            this.dateTime = value;
        }
    }

    private _setDateTimeToNull(): void {
        this.dateStruct = null;
        this.timeStruct = null;
        this.dateTime = null;
        this.onChange(this.dateTime);
    }

    private getFirstAvailableDate(date: Date): Date {
        if (date == null) {
            date = new Date();
        }
        const dateStruct = DatetimePickerComponent.getNgbDateStructFromDate(date);
        const current: { year: number; month: number } = {
            year: dateStruct.year,
            month: dateStruct.month
        };
        const ngbDate = (d: Date) => {
            const ds = DatetimePickerComponent.getNgbDateStructFromDate(d);
            return new NgbDate(ds.year, ds.month, ds.day);
        };
        let counter = 365 * 10;
        do {
            if (this.markDisabled(ngbDate(date), current) === false) {
                break;
            }
            date.setDate(date.getDate() + 1);
            counter -= 1;
        } while (counter > 0);

        if (counter === 0) {
            console.warn(`An available date has not been found in about 10 years`);
            return null;
        }
        return date;
    }

    private _updateValue() {
        console.debug(`updateValue`, this.dateStruct, this.timeStruct);
        if (this.dateStruct == null || this.timeStruct == null) {
            this._setDateTimeToNull();
        } else {
            try {
                this.dateTime = DatetimePickerComponent.getDateFromDateStructAndTimeStruct(
                    this.dateStruct,
                    this.timeStruct
                );
                this.onChange(this.dateTime);
            } catch (e) {
                console.error(`Exception trying to update dateTime. It will be set to null`, e);
                this._setDateTimeToNull();
            }
        }

        console.debug(`after the updateValue`, this.dateTime, this.dateStruct, this.timeStruct);
    }

    // dateStruct getter and setter
    get dateStruct(): NgbDateStruct {
        return this._dateStruct;
    }

    set dateStruct(value: NgbDateStruct) {
        if (value == null) {
            this._dateStruct = null;
            return;
        }
        if (value.day == null) {
            console.error(`Cannot set a 'null' day in dateStruct: '1' will be used instead `);
            value.day = 1;
        }
        if (value.month == null) {
            console.error(`Cannot set a 'null' month in dateStruct: '1' will be used instead`);
            value.month = 1;
        }
        if (value.year == null) {
            console.error(
                `Cannot set a 'null' year in dateStruct: current year will be used instead`
            );
            value.year = new Date().getFullYear();
        }
        this._dateStruct = value;
    }

    // timeStruct getter and setter
    get timeStruct(): NgbTimeStruct {
        return this._timeStruct;
    }

    set timeStruct(value: NgbTimeStruct) {
        if (value == null) {
            this._timeStruct = null;
            return;
        }
        if (value.hour == null) {
            console.error(`Cannot set a 'null' hour in timeStruct: '12' will be used instead `);
            value.hour = 12;
        }
        if (value.minute == null) {
            console.error(`Cannot set a 'null' minute in timeStruct: '0' will be used instead `);
            value.minute = 0;
        }
        if (value.second == null) {
            console.error(`Cannot set a 'null' second in timeStruct: '0' will be used instead `);
            value.second = 0;
        }
        if (!this.pickSeconds) {
            value.second = 0;
        }
        this._timeStruct = value;
    }

    // template getters
    get showClearButton(): boolean {
        return this.dateTime != null && !this.disabled;
    }

    get showDatePicker(): boolean {
        return this.pick === 'date' || this.pick === 'date-time';
    }

    get showTimePicker(): boolean {
        return this.pick === 'time' || this.pick === 'date-time';
    }

    // events
    private _onDateStructChangeHandler(event: NgbDateStruct) {
        //update timeStruct accordingly to timeZoneOffset changes (on dateStruct change)
        if (event != null && this.dateTime != null) {
            const deltaTimeZoneOffset = this.dateTime.getTimezoneOffset() - DatetimePickerComponent.getDateFromDateStructAndTimeStruct(event, this.timeStruct).getTimezoneOffset();
            const deltaTimeZoneOffsetSign = Math.sign(deltaTimeZoneOffset);
            if (deltaTimeZoneOffset !== 0) {
                console.warn(`The timeZoneOffset has found to have changed of ${deltaTimeZoneOffsetSign > 0 ? '+' : '-'}${Math.abs(deltaTimeZoneOffset)} minutes`);
                /* Separa il l'offset in ore e minuti CON SEGNO!!*/
                const minutesOffset = deltaTimeZoneOffset % 60;
                const hoursOffsets = (deltaTimeZoneOffset - minutesOffset) / 60;
                /* Somma algebrica dei minuti del timepicker (sempre positivi) e del cambio timeZoneOffset */
                const minutesSum = this.timeStruct.minute + minutesOffset; // Per questo numero vale -60<x<120
                /* Numero di minuti che l'orologio dovrà segnare NUMERO POSITIVO */
                const newMinutes = minutesSum >= 0 ? minutesSum % 60 : 60 - (-minutesSum % 60); // Per questo numero vale x>=0
                /* Ore di resto derivanti dalla somma dei minuti: vanno aggiunte algebricamente alle ore di offset */
                const hourReminder = minutesSum >= 0 ? (minutesSum - newMinutes) / 60 : -1 + (-minutesSum - newMinutes) / 60;
                /* Somma algebrica delle ore del timepicker (sempre positive) e del cambio timeZoneOffset e del resto delle ore calcolato dai minuti di offset */
                const hoursSum = this.timeStruct.hour + hoursOffsets + hourReminder;
                /* Numero di ore che l'orologio dovrà segnare NUMERO POSITIVO*/
                const newHours = hoursSum >= 0 ? hoursSum % 24 : 24 - (-hoursSum % 24);
                /* Numero di giorni di offset */
                const daysReminder = hoursSum >= 0 ? (hoursSum - newHours) / 24 : -1 + (-hoursSum - newHours) / 24;
                if (daysReminder !== 0) {
                    console.error(`The offset change caused a day change: this is not handled yet`);
                }
                console.debug(
                    `minutesOffset=${minutesOffset}\n` +
                    `hoursOffsets=${hoursOffsets}\n` +
                    `minutesSum=${minutesSum}\n` +
                    `newMinutes=${newMinutes}\n` +
                    `hourReminder=${hourReminder}\n` +
                    `hoursSum=${hoursSum}\n` +
                    `newHours=${newHours}\n` +
                    `daysReminder=${daysReminder}`
                );

                this.timeStruct = {
                    hour: newHours,
                    minute: newMinutes,
                    second: this.timeStruct.second
                };
            }
        }

        this.dateStruct = event;
        this._updateValue();
    }

    onDateStructChange(event: NgbDateStruct) {
        console.debug(`onDateStructChange`, event);
        this._onDateStructChangeHandler(event);
    }

    private _onTimeStructChangeHandler(event: NgbTimeStruct) {
        this.timeStruct = event;
        this._updateValue();
    }

    onTimeStructChange(event: NgbTimeStruct) {
        console.debug(`onTimeStructChange`, event);
        this._onTimeStructChangeHandler(event);
    }

    onDateNavigate(event: NgbDatepickerNavigateEvent) {
        console.debug(`onDateNavigate`, event);
        const dateStruct: NgbDateStruct = {
            year: event.next.year,
            month: event.next.month,
            // cambiando mese, se il giorno indicato non esiste, seleziona il primo del mese
            day: this.dateStruct != null ? this.dateStruct.day : 1
        };
        if (!DatetimePickerComponent.isValid(dateStruct, this.timeStruct)) {
            console.warn(
                `The date ${JSON.stringify(dateStruct)} has been found not to be a valid date`
            );
            dateStruct.day = 1;
        }
        this._onDateStructChangeHandler(dateStruct);
    }

    onPopShow() {
        console.debug(`onPopShow`);
        if (this.dateTime == null) {
            const newDate = this.getFirstAvailableDate(new Date());
            console.debug(`dateTime is null: new Date will be `, newDate);
            this.writeValue(newDate);
            this._updateValue();
        } else if (!this.disabled) {
            const firstAvailableDate = this.getFirstAvailableDate(this.dateTime);
            if (this.dateTime.valueOf() !== firstAvailableDate.valueOf()) {
                console.debug(`dateTime is not allowed: new Date will be `, firstAvailableDate);
                this.writeValue(firstAvailableDate);
                this._updateValue();
            }
        }
    }

    onClickResetButton() {
        console.debug(`onClickResetButton`);
        if (this.disabled) {
            return;
        }
        this._setDateTimeToNull();
    }

    onClickPickerButton() {
        console.debug(`onClickPickerButton`);
        if (this.disabled) {
            return;
        }
        if (!this.popInput.isOpen()) {
            this.popInput.open();
        }
    }

    onDone() {
        if (this.popInput.isOpen()) {
            this.popInput.close();
        }
    }

    onKeyDown(event: KeyboardEvent) {
        event.preventDefault();
    }
}



// WEBPACK FOOTER //
// ./src/app/components/datetime-picker/datetime-picker.component.ts